import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { sharedStyles } from '../styles/alfredStyles';
import { onAuthStateChanged } from 'firebase/auth';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.warn('⚠️ No authenticated user found.');
        return;
      }

      try {
        const docRef = doc(db, 'users', user.uid); // 🔑 uses UID, matches your Firestore doc
        const snap = await getDoc(docRef);

        if (snap.exists()) {
          setUserData({ email: user.email, ...snap.data() });
        } else {
          console.warn('⚠️ No Firestore profile found for this user.');
          setUserData({ email: user.email }); // fallback: let them fill in the blanks
        }
      } catch (err) {
        console.error('❌ Error fetching user profile:', err);
      }
    });

    return () => unsubscribe(); // cleanup
  }, []);

  const handleChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!userData) return;
    setSaving(true);
    const { email, createdAt, ...editable } = userData;

    try {
      const currentUser = auth.currentUser;
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, editable);
      alert('Profile updated!');
    } catch (err) {
      console.error('Update error:', err);
      alert('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (!userData) return <div style={{ padding: '40px' }}>Loading profile...</div>;

  return (
    <div style={{ padding: '40px', maxWidth: '760px', margin: '0 auto', fontFamily: `'Helvetica Neue', sans-serif` }}>
      <h2 style={{ marginBottom: '32px' }}>Your Profile</h2>

      {/* 🔁 Aligned Input Pairs */}
      {[
        ['First Name', 'firstName', 'Last Name', 'lastName'],
        ['Company Name', 'companyName', 'Department Name', 'departmentName'],
        ['Phone Number', 'phoneNumber', 'Birthday', 'birthday', '', 'date'],
        ['Email', 'email', 'Status', 'status', 'readonly', 'readonly-disabled'],
       ].map(([label1, field1, label2, field2, type1, type2]) => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }} key={field1}>
          {/* Left Field */}
          <div style={sharedStyles.inputWrapper}>
            <label style={sharedStyles.label}>{label1}</label>
            <input
              type={type1 || 'text'}
              style={field1 === 'email' ? { ...sharedStyles.input, backgroundColor: '#eee' } : sharedStyles.input}
              value={userData[field1] || ''}
              onChange={(e) => handleChange(field1, e.target.value)}
              disabled={field1 === 'email'}
            />
          </div>

          {/* Right Field */}
          <div style={sharedStyles.inputWrapper}>
            <label style={sharedStyles.label}>{label2}</label>
            <input
              type={type2 || 'text'}
              style={(field2 === 'accessTier' || field2 === 'status' || field2 === 'email') 
                ? { ...sharedStyles.input, backgroundColor: '#eee' } 
                : sharedStyles.input}
              value={userData[field2] || ''}
              onChange={(e) => handleChange(field2, e.target.value)}
              disabled={field2 === 'accessTier' || field2 === 'status' || field2 === 'email'}
            />
          </div>
        </div>
      ))}

      {/* 🕒 Created At */}
      <div style={sharedStyles.inputWrapper}>
        <label style={sharedStyles.label}>Created At</label>
        <input
          style={{ ...sharedStyles.input, backgroundColor: '#eee' }}
          value={userData.createdAt?.toDate().toLocaleString() || 'N/A'}
          disabled
        />
      </div>

      {/* 💾 Save Button */}
      <button
        style={{ ...sharedStyles.button, marginTop: '24px' }}
        onClick={handleSave}
      >
        {saving ? 'Saving...' : 'Save Profile'}
      </button>
    </div>
  );
};

export default Profile;
